import type { IBrand, IMarket } from 'data/schema';
import { marketAliases, brandAliases } from './marketSearchAliases';

const GERMAN_LIGATURES: [RegExp, string][] = [
  [/ü/g, 'ue'],
  [/Ü/g, 'Ue'],
  [/ö/g, 'oe'],
  [/Ö/g, 'Oe'],
  [/ä/g, 'ae'],
  [/Ä/g, 'Ae'],
  [/ß/g, 'ss'],
];

/** Strip diacritics via NFD decomposition. */
function stripDiacritics(text: string): string {
  return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

/** Strip everything except letters and digits, then lowercase. */
function stripAndLower(text: string): string {
  return text.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
}

/**
 * Full normalization: expand German ligatures, strip diacritics, lowercase, strip non-alnum.
 * "München" → "muenchen"
 */
export function normalize(text: string): string {
  if (!text) return '';
  let result = text;
  for (const [pattern, replacement] of GERMAN_LIGATURES) {
    result = result.replace(pattern, replacement);
  }
  return stripAndLower(stripDiacritics(result));
}

/**
 * Simple normalization: strip diacritics only (no ligature expansion), lowercase, strip non-alnum.
 * "München" → "munchen"
 */
export function normalizeSimple(text: string): string {
  if (!text) return '';
  return stripAndLower(stripDiacritics(text));
}

export interface SearchableRow {
  brand: IBrand;
  market: IMarket;
  tenant: string;
  tenantName?: string;
}

/**
 * Build normalized alias arrays (both forms) for a record of aliases.
 * Each alias produces two normalized forms: expanded (normalize) and simple (normalizeSimple).
 * The code itself is also included.
 */
function buildIndex(aliases: Record<string, string[]>): Map<string, string[]> {
  const index = new Map<string, string[]>();
  for (const [code, names] of Object.entries(aliases)) {
    const normalized = new Set<string>();
    normalized.add(normalize(code));
    normalized.add(normalizeSimple(code));
    for (const name of names) {
      normalized.add(normalize(name));
      normalized.add(normalizeSimple(name));
    }
    index.set(code, [...normalized]);
  }
  return index;
}

const normalizedMarketIndex = buildIndex(marketAliases);
const normalizedBrandIndex = buildIndex(brandAliases);

/**
 * Creates a filter predicate from a search query.
 * Empty query matches everything.
 * A row matches if the normalized query is a substring of any alias
 * for its market, brand, tenant ID, or tenant name.
 */
export function createMarketFilter(query: string): (row: SearchableRow) => boolean {
  if (!query.trim()) return () => true;

  const normalizedQuery = normalize(query);
  const simpleQuery = normalizeSimple(query);

  return (row: SearchableRow): boolean => {
    const marketAliasNorms = normalizedMarketIndex.get(row.market);
    if (marketAliasNorms) {
      for (const alias of marketAliasNorms) {
        if (alias.includes(normalizedQuery) || alias.includes(simpleQuery)) return true;
      }
    }

    const brandAliasNorms = normalizedBrandIndex.get(row.brand);
    if (brandAliasNorms) {
      for (const alias of brandAliasNorms) {
        if (alias.includes(normalizedQuery) || alias.includes(simpleQuery)) return true;
      }
    }

    const tenantNorm = normalize(row.tenant);
    const tenantSimple = normalizeSimple(row.tenant);
    if (tenantNorm.includes(normalizedQuery) || tenantSimple.includes(simpleQuery)) return true;

    if (row.tenantName) {
      const nameNorm = normalize(row.tenantName);
      const nameSimple = normalizeSimple(row.tenantName);
      if (nameNorm.includes(normalizedQuery) || nameSimple.includes(simpleQuery)) return true;
    }

    return false;
  };
}
